import { useEffect, useMemo, useState } from "react";
import styles from "./HomesteadExemptionPage.module.css";
import { HomesteadSuccessModal } from "../SucessModal/HomesteadSuccessModal";
import { useHomesteadExemptionViewModal } from "@/features/homesteadExemption/viewModal/homesteadExemptionViewModal";
import { Modal } from "@/shared/modal";
import { heroImage } from "@/assets/images";
import { usePropertyStreetViews, type PropertyLike } from "@/shared/utils";

export const HomesteadExemptionPage = () => {
  const vm = useHomesteadExemptionViewModal(styles);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [checkoutConsentAccepted, setCheckoutConsentAccepted] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Dynamic property image resolution via Google Street View or backend photo fields
  const propertyList = useMemo<PropertyLike[]>(() => {
    if (!vm.propertyDetails) return [];
    return [vm.propertyDetails];
  }, [vm.propertyDetails]);

  const streetViewImages = usePropertyStreetViews(propertyList);

  const rawProp = vm.propertyDetails as Record<string, unknown> | null;
  const directImageUrl =
    (typeof rawProp?.image_url === "string" && rawProp.image_url) ||
    (typeof rawProp?.property_photo === "string" && rawProp.property_photo) ||
    (typeof rawProp?.photo_url === "string" && rawProp.photo_url) ||
    (typeof rawProp?.image === "string" && rawProp.image) ||
    (typeof rawProp?.property_image === "string" && rawProp.property_image) ||
    (typeof rawProp?.primary_photo === "string" && rawProp.primary_photo) ||
    null;

  const currentPropertyId = vm.propertyDetails?.property_id || vm.propertyId;
  const streetViewUrl = currentPropertyId ? streetViewImages[currentPropertyId] : null;
  const dynamicPropertyImage = directImageUrl || streetViewUrl || heroImage;

  useEffect(() => {
    setImageError(false);
  }, [directImageUrl, streetViewUrl]);

  const finalImageSrc = imageError ? heroImage : dynamicPropertyImage;

  const isAlreadyFiled =
    vm.propertyDetails?.homestead_status !== "not_filed" &&
    vm.propertyDetails?.homestead_status !== null;

  useEffect(() => {
    if (!vm.isSignModalOpen) {
      setConsentAccepted(false);
    }
  }, [vm.isSignModalOpen]);

  const signModalTitle = vm.propertyDetails?.homestead_signed
    ? "Signed Homestead Documents"
    : "Provide Digital Signature";

  const signModalBody = vm.propertyDetails?.homestead_signed ? (
    <div className={styles.documentsGridSingle}>
      <div className={styles.documentPanel}>
        <div className={styles.documentTitle}>Homestead Document</div>
        {vm.homesteadDocUrl ? (
          <iframe
            title="Homestead Document"
            src={vm.homesteadDocUrl}
            className={styles.documentFrame}
          />
        ) : (
          <div className={styles.documentEmpty}>Document preview unavailable.</div>
        )}
      </div>
    </div>
  ) : (
    <div className={styles.signatureSection}>
      <div className={styles.signatureHeaderBlock}>
        <div className={styles.signatureTitle}>Your Signature</div>
        <p className={styles.signatureSubtitle}>
          Please sign below to complete your Homestead / Over 65 Exemption documents.
        </p>
        <ul className={styles.signatureBullets}>
          <li>Creates your Homestead / Over 65 Exemption application with the county.</li>
          <li>Completes the required forms for exemption filing.</li>
          <li>Helps reduce your property tax burden through legal exemptions.</li>
        </ul>
      </div>

      <div className={styles.filingBlock}>
        <div className={styles.filingTitle}>Property Tax Exemption Filing</div>
        <div className={styles.docsToSign}>
          <div className={styles.docsToSignTitle}>Documents to Sign</div>
          <p className={styles.docsToSignSubtitle}>
            Your signature will be used to complete the following required documents:
          </p>

          <div className={styles.docList}>
            <div className={styles.docItem}>
              <span className={styles.docIcon} aria-hidden="true">
                🏠
              </span>
              <div className={styles.docText}>
                <div className={styles.docName}>Homestead / Over 65 Exemption Application</div>
                <div className={styles.docDesc}>
                  Your official application for homestead and over 65 tax exemptions with the
                  county.
                </div>
              </div>
            </div>

            <div className={styles.docItem}>
              <span className={styles.docIcon} aria-hidden="true">
                📄
              </span>
              <div className={styles.docText}>
                <div className={styles.docName}>Exemption Filing Agreement</div>
                <div className={styles.docDesc}>
                  Agreement for filing your exemption application with the appropriate authorities.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.signatureCanvasTitle}>Digital Signature</div>
      <canvas
        ref={vm.signatureCanvasRef}
        className={styles.signatureCanvas}
        onMouseDown={vm.startDrawing}
        onMouseMove={vm.draw}
        onMouseUp={vm.stopDrawing}
        onMouseLeave={vm.stopDrawing}
        onTouchStart={vm.startDrawing}
        onTouchMove={vm.draw}
        onTouchEnd={vm.stopDrawing}
      />
      <div className={styles.signatureActions}>
        <label className={styles.consentRow} htmlFor="homestead-sign-consent">
          <input
            id="homestead-sign-consent"
            type="checkbox"
            className={styles.consentCheckbox}
            checked={consentAccepted}
            onChange={(event) => setConsentAccepted(event.target.checked)}
          />
          <span className={styles.consentText}>
            I agree to exemption to filing terms &amp; authorize The Andy App to process my
            Homestead/Over 65 Exemption application
          </span>
        </label>
        <button type="button" className={styles.clearBtn} onClick={vm.clearSignature}>
          Clear
        </button>
        <button
          type="button"
          className={styles.signBtn}
          onClick={() => void vm.handleSubmitSignature()}
          disabled={!vm.signature || vm.isSubmittingSignature || !consentAccepted}
        >
          {vm.isSubmittingSignature ? "Submitting..." : "Submit Signature"}
        </button>
      </div>
    </div>
  );

  return (
    <section className={styles.page}>
      <div className={styles.container}>
        <div className={styles.topBar}>
          <button type="button" className={styles.backButton} onClick={vm.onBack}>
            <span className={styles.backArrow}>←</span> Back
          </button>
        </div>

        <div className={styles.pageHeader}>
          <h1 className={styles.title}>Homestead Exemption</h1>
          <p className={styles.subTitle}>
            Upload required documents to file for Homestead / Over 65 exemption for your property.
          </p>
        </div>

        {/* Property Address Card */}
        <div className={styles.addressCard}>
          <div className={styles.addressLeft}>
            <img
              src={finalImageSrc}
              alt={vm.propertyAddressTitle || "Property"}
              className={styles.propertyThumb}
              loading="lazy"
              onError={() => setImageError(true)}
            />

            <div className={styles.addressBody}>
              <h2 className={styles.addressTitle}>
                {vm.isPropertyLoading ? "Loading property..." : vm.propertyAddressTitle}
              </h2>

              <div className={styles.addressLocationRow}>
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#00a86b"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                <span>
                  {vm.isPropertyLoading
                    ? "Please wait..."
                    : vm.propertyAddressMeta || vm.propertyId || "No property selected"}
                </span>
              </div>

              <div className={styles.addressTagsRow}>
                <span className={styles.addressPill}>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                    <polyline points="9 22 9 12 15 12 15 22" />
                  </svg>
                  Primary Residence
                </span>

                <span className={styles.addressPill}>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                  Eligible for Homestead / Over 65
                </span>
              </div>
            </div>
          </div>

          <button
            type="button"
            className={styles.viewBtn}
            onClick={() => vm.setIsSignModalOpen(true)}
          >
            <span className={styles.viewBtnIcon} aria-hidden="true">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <line x1="10" y1="9" x2="8" y2="9" />
              </svg>
            </span>
            <div className={styles.viewBtnText}>
              <span>{vm.propertyDetails?.homestead_signed ? "View Signed" : "Sign"}</span>
              <span>Homestead/Over 65</span>
              <span>Exemption</span>
            </div>
          </button>
        </div>

        {/* 2-Column Content Grid */}
        <div className={styles.grid}>
          {/* Left Column */}
          <div className={styles.leftCol}>
            {/* Upload Driver's License Card */}
            <div className={styles.uploadCard}>
              <div className={styles.cardHeaderWithBadge}>
                <div className={styles.cardHeaderIconBadge} aria-hidden="true">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                </div>
                <div>
                  <h2 className={styles.sectionTitle}>Upload your Driver&apos;s License Images</h2>
                  <p className={styles.sectionSubTitle}>
                    Your address on the license must match the property address.
                  </p>
                </div>
              </div>

              <div className={styles.uploadRow}>
                {/* Front of License */}
                <button
                  type="button"
                  className={styles.uploadBox}
                  onClick={() => vm.frontInputRef.current?.click()}
                  disabled={vm.isUploadingFront}
                >
                  {vm.licenseFrontPhoto ? (
                    <img
                      src={vm.licenseFrontPhoto}
                      alt="Front license preview"
                      className={styles.licensePreview}
                    />
                  ) : vm.isUploadingFront ? (
                    <span className={styles.uploadStatusText}>Uploading...</span>
                  ) : (
                    <div className={styles.uploadPrompt}>
                      <div className={styles.uploadPromptIcon} aria-hidden="true">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <polyline points="21 15 16 10 5 21" />
                        </svg>
                      </div>
                      <div className={styles.uploadPromptTitle}>
                        {vm.frontFileName || "Front of License"}
                      </div>
                      <div className={styles.uploadPromptSub}>Click to upload or drag and drop</div>
                      <div className={styles.uploadPromptHint}>JPG, PNG, PDF (Max 5MB)</div>
                    </div>
                  )}
                </button>

                {/* Back of License */}
                <button
                  type="button"
                  className={styles.uploadBox}
                  onClick={() => vm.backInputRef.current?.click()}
                  disabled={vm.isUploadingBack}
                >
                  {vm.licenseBackPhoto ? (
                    <img
                      src={vm.licenseBackPhoto}
                      alt="Back license preview"
                      className={styles.licensePreview}
                    />
                  ) : vm.isUploadingBack ? (
                    <span className={styles.uploadStatusText}>Uploading...</span>
                  ) : (
                    <div className={styles.uploadPrompt}>
                      <div className={styles.uploadPromptIcon} aria-hidden="true">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <polyline points="21 15 16 10 5 21" />
                        </svg>
                      </div>
                      <div className={styles.uploadPromptTitle}>
                        {vm.backFileName || "Back of License"}
                      </div>
                      <div className={styles.uploadPromptSub}>Click to upload or drag and drop</div>
                      <div className={styles.uploadPromptHint}>JPG, PNG, PDF (Max 5MB)</div>
                    </div>
                  )}
                </button>

                <input
                  ref={vm.frontInputRef}
                  type="file"
                  className={styles.hiddenInput}
                  accept="image/*,.pdf,.doc,.docx"
                  onChange={(event) =>
                    void vm.handleUpload("front", event.target.files?.[0] ?? null)
                  }
                />
                <input
                  ref={vm.backInputRef}
                  type="file"
                  className={styles.hiddenInput}
                  accept="image/*,.pdf,.doc,.docx"
                  onChange={(event) =>
                    void vm.handleUpload("back", event.target.files?.[0] ?? null)
                  }
                />
              </div>
            </div>

            {/* Why this works Card */}
            <div className={styles.whyCard}>
              <div className={styles.cardHeaderWithBadge}>
                <div className={styles.cardHeaderIconBadge} aria-hidden="true">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="9" y1="18" x2="15" y2="18" />
                    <line x1="10" y1="22" x2="14" y2="22" />
                    <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14" />
                  </svg>
                </div>
                <div className={styles.whyTitle}>Why this works</div>
              </div>

              <div className={styles.whyGrid}>
                <div className={styles.whyItem}>
                  <div className={styles.whyItemIcon} aria-hidden="true">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      <polyline points="9 12 11 14 15 10" />
                    </svg>
                  </div>
                  <div>
                    <div className={styles.whyItemTitle}>Filed directly</div>
                    <div className={styles.whyItemSub}>With county offices</div>
                  </div>
                </div>

                <div className={styles.whyItem}>
                  <div className={styles.whyItemIcon} aria-hidden="true">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <ellipse cx="12" cy="5" rx="9" ry="3" />
                      <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
                      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
                    </svg>
                  </div>
                  <div>
                    <div className={styles.whyItemTitle}>One-time filing</div>
                    <div className={styles.whyItemSub}>No recurring fees</div>
                  </div>
                </div>

                <div className={styles.whyItem}>
                  <div className={styles.whyItemIcon} aria-hidden="true">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                      <polyline points="17 6 23 6 23 12" />
                    </svg>
                  </div>
                  <div>
                    <div className={styles.whyItemTitle}>Saves you money</div>
                    <div className={styles.whyItemSub}>Helps reduce your property taxes</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Summary Card */}
          <aside className={styles.summaryCard}>
            <div className={styles.cardHeaderWithBadge}>
              <div className={styles.cardHeaderIconBadge} aria-hidden="true">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
              </div>
              <div>
                <h3 className={styles.summaryTitle}>Exemption Filing Summary</h3>
                <p className={styles.summarySubTitle}>
                  Review your filing details and total amount
                </p>
              </div>
            </div>

            <div className={styles.summaryRowsList}>
              <div className={styles.summaryRow}>
                <div className={styles.summaryLabelGroup}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#64748b"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  </svg>
                  <span>Property</span>
                </div>
                <span className={styles.summaryValue}>
                  {vm.isPropertyLoading ? "Loading..." : vm.propertyAddressTitle}
                </span>
              </div>

              <div className={styles.summaryRow}>
                <div className={styles.summaryLabelGroup}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#64748b"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  </svg>
                  <span>Exemption</span>
                </div>
                <span className={styles.summaryValue}>Homestead / Over 65</span>
              </div>

              <div className={styles.summaryRow}>
                <div className={styles.summaryLabelGroup}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#64748b"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <line x1="2" y1="10" x2="22" y2="10" />
                  </svg>
                  <span>County Filing Fee</span>
                </div>
                <span className={styles.summaryValue}>Included</span>
              </div>
            </div>

            {!isAlreadyFiled ? (
              <div className={styles.totalRow}>
                <span className={styles.totalLabel}>Total</span>
                <strong className={styles.totalPrice}>$149</strong>
              </div>
            ) : null}

            {isAlreadyFiled ? (
              <>
                <div className={styles.alreadyFiledMessage}>
                  Homestead/Over 65 Exemption has already been filed for this property
                </div>
                <button
                  type="button"
                  className={styles.checkoutBtn}
                  onClick={() => {
                    const hasFront = Boolean(vm.licenseFrontPhoto);
                    const hasBack = Boolean(vm.licenseBackPhoto);
                    const hasSigned = vm.propertyDetails?.homestead_signed;
                    if (!hasFront) {
                      window.alert(
                        "Please upload the FRONT of your driver’s license before continuing."
                      );
                      return;
                    }
                    if (!hasBack) {
                      window.alert(
                        "Please upload the BACK of your driver’s license before continuing."
                      );
                      return;
                    }
                    if (!hasSigned) {
                      vm.setIsSignModalOpen(true);
                      window.alert("Please sign the homestead documents before continuing.");
                      return;
                    }
                    vm.navigate(vm.routes.homepage);
                  }}
                >
                  Continue
                </button>
              </>
            ) : (
              <>
                <label className={styles.checkoutConsentRow} htmlFor="homestead-checkout-consent">
                  <input
                    id="homestead-checkout-consent"
                    type="checkbox"
                    className={styles.consentCheckbox}
                    checked={checkoutConsentAccepted}
                    onChange={(event) => setCheckoutConsentAccepted(event.target.checked)}
                  />
                  <span className={styles.consentText}>
                    I confirm that the information provided is accurate, and I agree to{" "}
                    <span className={styles.consentLink}>Exemption Filing agreement</span>
                  </span>
                </label>

                <button
                  type="button"
                  className={styles.checkoutBtn}
                  onClick={() => void vm.onStartCheckout()}
                  disabled={vm.isPaymentProcessing || !checkoutConsentAccepted}
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  <span>Checkout</span>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </button>
              </>
            )}
          </aside>
        </div>
      </div>

      <Modal
        isOpen={vm.isSignModalOpen}
        onClose={() => vm.setIsSignModalOpen(false)}
        unconstrained
        className={styles.modalCard}
        headerClassName={styles.modalHeader}
        title={signModalTitle}
        titleClassName={styles.modalTitle}
        bodyClassName={styles.modalCardBody}
        ariaLabel="Sign homestead documents"
      >
        {signModalBody}
      </Modal>

      {vm.paymentModal}

      <HomesteadSuccessModal
        isOpen={vm.isSuccessOpen}
        onClose={() => vm.setIsSuccessOpen(false)}
        onDone={() => vm.navigate(vm.routes.homepage)}
      />
    </section>
  );
};
